import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ConfigProvider, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchAllAdminDashboardData, setActivities, AdminDashboardState } from '../../../redux/AdminDashboard';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { ThemeProvider } from 'styled-components';
import {
  METRIC_TYPES,
  AUTO_REFRESH_INTERVAL,
  MERCHANT_TABLE_COLUMNS,
  DEVICE_TABLE_COLUMNS,
  SECTION_TITLES,
} from './constants';
import { formatTimeAgo } from './utils';
import { Container, LoadingContainer, BottomGrid } from './styles';
import HeaderSection from './HeaderSection';
import QuickActionsSection from './QuickActionsSection';
import MetricCardsSection from './MetricCardsSection';
import EnhancedTableSection from './EnhancedTableSection';
import RecentActivitiesSection from './RecentActivitiesSection';
import SystemHealthSection from './SystemHealthSection';

const DashboardAdmin: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const dispatch = useDispatch<AppDispatch>();
  const hasFetchedRef = useRef(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const {
    merchants,
    devices,
    activities,
    systemHealth,
    loading,
  } = useSelector((state: RootState): AdminDashboardState => state.adminDashboard);

  const isLoading = loading.merchants || loading.devices;

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchAllAdminDashboardData());
    }
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activities.length > 0) {
        const updated = activities.map((a) => ({
          ...a,
          time: formatTimeAgo(new Date(Date.now() - Math.random() * 30 * 60 * 1000)),
        }));
        dispatch(setActivities(updated));
      }
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [activities, dispatch]);

  const handleTileClick = (key: string) => {
    setSelectedItem(key === selectedItem ? null : key);
  };

  const handleRefresh = useCallback(() => {
    dispatch(fetchAllAdminDashboardData());
  }, [dispatch]);

  const merchantTableData = merchants.map((m) => ({
    name: m.name,
    email: m.email,
    phone: m.phone,
    status: true,
  }));

  const deviceTableData = devices.map((d) => ({
    name: d.name,
    identifier: d.identifier,
    type: d.type?.name || 'Unknown',
    status: true,
  }));

  if (isLoading && merchants.length === 0) {
    return (
      <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection />
          <QuickActionsSection />

          <MetricCardsSection
            totalMerchants={merchants.length}
            activeDevices={devices.length}
            isLoading={isLoading}
            selectedItem={selectedItem}
            onTileClick={handleTileClick}
          />

          <EnhancedTableSection
            isOpen={selectedItem === METRIC_TYPES.TOTAL_MERCHANTS}
            data={merchantTableData}
            columns={MERCHANT_TABLE_COLUMNS}
            title={SECTION_TITLES.MERCHANTS_OVERVIEW}
            onClose={() => handleTileClick(METRIC_TYPES.TOTAL_MERCHANTS)}
            isLoading={isLoading}
          />

          <EnhancedTableSection
            isOpen={selectedItem === METRIC_TYPES.ACTIVE_DEVICES}
            data={deviceTableData}
            columns={DEVICE_TABLE_COLUMNS}
            title={SECTION_TITLES.ACTIVE_DEVICES}
            onClose={() => handleTileClick(METRIC_TYPES.ACTIVE_DEVICES)}
            isLoading={isLoading}
          />

          <BottomGrid>
            <RecentActivitiesSection
              activities={activities}
              isLoading={loading.activities}
              onRefresh={handleRefresh}
            />
            <SystemHealthSection systemHealth={systemHealth} />
          </BottomGrid>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default DashboardAdmin;
